import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { LOGIN_PAGE } from "../../configuration/paths";
import { getAppVersions, getProfile, getSingleAlert, getUnitNames, getVehicleAlerts, getVehicleDeviceMap } from '../../store';
import { DetectionModal } from '../utility/components/DetectionModal';
import { VideoModal } from '../utility/components/VideoModal';
import useIsMobile from '../../hooks/useIsMobile';
import AlarmHistoryMobile from './AlarmHistoryMobile';
import AlarmHistoryDesktop from './AlarmHistoryDesktop';
import { alertKey } from './components/alertKey';

const Index = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const loggedIn = !!useSelector(state => state.app.auth.token);
    const isSpecial = useSelector(state => state.app.profile.role === 'special');
    const alerts = useSelector(state => state.app.alerts);
    const isMobile = useIsMobile();
    const [loading, setLoading] = useState(true);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [units, setUnits] = useState([]);
    const [appVersions, setAppVersions] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedLevels, setSelectedLevels] = useState([]);
    const [selectedAppVersions, setSelectedAppVersions] = useState([]);
    const [selectedRange, setSelectedRange] = useState([]);
    // { vehicle_name: [serial, ...] } reported in the selected range — drives the
    // Vehicle -> ROADZ Device auto-select. Refreshed whenever the range changes.
    const [vehicleDeviceMap, setVehicleDeviceMap] = useState({});
    const [sortBy, setSortBy] = useState('-detection_date');
    // Next 5 are for the modal
    const [alertModalOpen, setAlertModalOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [selectedCounter, setSelectedCounter] = useState(-1);
    // Session review-state: which alerts have been opened, so the list shows what you've
    // already looked at (the last-opened gets the strong "selected" highlight; earlier ones
    // a subtle "viewed" mark) — you don't lose your place after closing the popup.
    const [viewedKeys, setViewedKeys] = useState(() => new Set());
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [url, setUrl] = useState('');
    const [selectedInterval, setSelectedInterval] = useState('Past 24 Hours');
    const INTERVALS = [
        'Past 24 Hours',
        'Past Week',
        'Past Month',
        'Custom'
    ]
    const typeDict = {
        None: 'Near Miss Report',
        Debug: 'Debug Report',
        Manual: 'Manual Report',
        Tablet: 'Tablet Report',
        Automatic: 'Automatic Report',
        "FA/NM": 'False Alarm/ Near Miss'
    }
    const levelDict = {
        0: 'No Hazard',
        1: 'Lights',
        2: 'Sirens',
    }


    const compareAppVersions = (aV1, aV2) => {
        let splitAV1 = aV1.split('.').map((num) => parseInt(num))
        let splitAV2 = aV2.split('.').map((num) => parseInt(num))

        let diff = [splitAV1[0] - splitAV2[0], splitAV1[1] - splitAV2[1], splitAV1[2] - splitAV2[2]]

        if (diff[0] !== 0) {
            return -1 * diff[0]
        } else if (diff[1] !== 0) {
            return -1 * diff[1]
        } else {
            return -1 * diff[2]
        }

    }

    const grabUpdates = () => {
        dispatch(getVehicleAlerts({ vehicles: selectedVehicles, devices: selectedDevices, type: selectedTypes, level: selectedLevels, app_version: selectedAppVersions, range: selectedRange, sortBy: sortBy }));
    }

    const parseFilterDate = (range) => {
        const formatDate = (date) => {
            return new Date(date.replace('_', ' ')).toISOString().replace('Z', '+00:00')
        }
        if (!!range) {
            range = range.replaceAll('_', " ").split('-')
            return [formatDate(range[0].replace('_', ' ')), formatDate(range[1].replace('_', ' '))]
        }
    }

    const findAlert = (unit, alert) => {
        const theAlert = alerts?.find(a => a[1].serial_number === unit && a[1].detection_date === alert)
        if (!!theAlert) {
            setSelectedCounter(theAlert[0])
            setSelectedAlert(theAlert[1])
            return true
        } else {
            return false
        }
    }

    const getSetState = (filterType) => {
        if (filterType === "vehicleFilters") {
            return setSelectedVehicles
        } else if (filterType === "deviceFilters") {
            return setSelectedDevices
        } else if (filterType === "triggerTypeFilters") {
            return setSelectedTypes
        } else if (filterType === "triggerLevelFilters") {
            return setSelectedLevels
        } else if (filterType === "appVersionFilters") {
            return setSelectedAppVersions
        }
    }

    const manageSelectedKeys = (e) => {
        // Chip identity is (key, category). Device serial and Vehicle name can be the same
        // string (e.g. "LTN0028" — a unit whose old vehicle name matched its serial), so we
        // MUST compare both fields or selecting one chip toggles the same-named other.
        let alreadySelected = false;
        if (!(e.key === 'selected')) {
            const cat = e.keyPath[1]
            if (!selectedKeys.find(k => k[0] === e.key && k[1] === cat)) {
                setSelectedKeys(prevKeys => [...prevKeys, [e.key, cat]])
            } else {
                setSelectedKeys(selectedKeys.filter(k => !(k[0] === e.key && k[1] === cat)))
                alreadySelected = true;
            }
        }
        return alreadySelected
    }

    const manageFilter = (alreadySelected, setFilter, value) => {
        if (!alreadySelected) {
            setFilter(f => [...f, value])
        } else {
            setFilter(f => f.filter((val) => {
                return val !== value
            }))
        }
    }

    const onClick = (e) => {
        const STR_TO_INT = {
            'No Hazard': 0,
            'Lights': 1,
            'Sirens': 2
        }
        if (!loading) {
            let alreadySelected = manageSelectedKeys(e);
            if (e.keyPath[2] === 'filters') {
                if (e.keyPath[1] === 'vehicleFilters') {
                    manageFilter(alreadySelected, setSelectedVehicles, e.key)
                    // Selecting a Vehicle auto-selects the ROADZ Device(s) that reported under
                    // it during the chosen range (the user can then deselect any). Directional:
                    // selecting a Device does NOT auto-select a Vehicle. Functional updaters
                    // self-dedup so a double invoke / overlapping click can't add twice.
                    if (!alreadySelected) {
                        const serials = vehicleDeviceMap[e.key] || []
                        if (serials.length) {
                            setSelectedDevices(prev => [...prev, ...serials.filter(s => !prev.includes(s))])
                            setSelectedKeys(prev => [...prev, ...serials
                                .filter(s => !prev.some(k => k[0] === s && k[1] === 'deviceFilters'))
                                .map(s => [s, 'deviceFilters'])])
                        }
                    }
                } else if (e.keyPath[1] === 'deviceFilters') {
                    manageFilter(alreadySelected, setSelectedDevices, e.key)
                } else if (e.keyPath[1] === 'triggerTypeFilters') {
                    manageFilter(alreadySelected, setSelectedTypes, e.key)
                } else if (e.keyPath[1] === 'triggerLevelFilters') {
                    manageFilter(alreadySelected, setSelectedLevels, STR_TO_INT[e.key])
                } else if (e.keyPath[1] === 'appVersionFilters') {
                    manageFilter(alreadySelected, setSelectedAppVersions, e.key)
                }
            }
        }
    }

    // Native From/To date inputs (Custom interval): whole-day inclusive bounds.
    const onCustomDates = (fromStr, toStr) => {
        if (!fromStr || !toStr) return
        const start = new Date(`${fromStr}T00:00:00`).toISOString().replace('Z', '+00:00')
        const stop = new Date(`${toStr}T23:59:59`).toISOString().replace('Z', '+00:00')
        setSelectedRange([start, stop])
    }

    const flipDateOrName = (date, oldSeparator, newSeparator) => {
        if (date) {
            date = date.split('T')
            date[1] = date[1].replaceAll(oldSeparator, newSeparator)
            date = date.join('T')
        }
        return date
    }

    // Stable per-alert identity for the reviewed/selected highlight (serial + detection date).

    const handleDetectionClick = (alert) => {
        setSelectedAlert(alert)
        setViewedKeys(prev => {
            const next = new Set(prev)
            next.add(alertKey(alert))
            return next
        })
        navigate(`${location.pathname}?unit=${alert.serial_number}&alert=${flipDateOrName(alert.detection_date, ':', '-')}`)
    }

    const handleClose = () => {
        setAlertModalOpen(false);
        navigate(`${location.pathname}`)
    }

    const setInterval = (e) => {
        if (!loading) {
            setSelectedInterval(e.key)
        }
    }

    if (!loggedIn) {
        navigate(LOGIN_PAGE)
    }

    // Refresh the date-scoped vehicle->device association when the range changes.
    // Bounds sent as UTC-Z toISOString (matches the check_in_time sort key).
    useEffect(() => {
        if (selectedRange && selectedRange.length === 2 && selectedRange[0] && selectedRange[1]) {
            dispatch(getVehicleDeviceMap({
                start: new Date(selectedRange[0]).toISOString(),
                end: new Date(selectedRange[1]).toISOString(),
            })).then((r) => setVehicleDeviceMap(r.payload || {}))
        }
    }, [selectedRange]);

    useEffect(() => {
        dispatch(getProfile())
        dispatch(getUnitNames())
            .then((response) => {
                setUnits(response.payload.sort((n1, n2) => n1[1] > n2[1] ? 1 : -1))
            })
        dispatch(getAppVersions())
            .then((response) => {
                setAppVersions(response.payload.sort((aV1, aV2) => compareAppVersions(aV1, aV2)))
            })
    }, []);

    useEffect(() => {
        setAlertModalOpen(false)
        const searchParams = new URLSearchParams(location.search)
        const unit = searchParams.get('unit')
        const filter = searchParams.has('filter')
        if (filter) {
            const t = searchParams.get('type')
            const level = searchParams.get('level')
            const date = searchParams.get('range')

            setSelectedDevices([unit])
            setSelectedKeys([[unit, 'deviceFilters']])

            if (!!date) {
                setSelectedInterval('Custom')
                setSelectedRange(f => parseFilterDate(date))
            }

            if (t) {
                setSelectedTypes([t])
                setSelectedKeys(prevKeys => [...prevKeys, [t, 'triggerTypeFilters']])
            }

            if (level) {
                const INT_TO_STR = ['No Hazard', 'Lights', 'Sirens']
                setSelectedLevels([parseInt(level)])
                setSelectedKeys(prevKeys => [...prevKeys, [INT_TO_STR[parseInt(level)], 'triggerLevelFilters']])
            }
        } else {
            const alert = searchParams.get('alert')
            if (unit && alert) {
                findAlert(unit, alert)
                setAlertModalOpen(true)
            }
        }
        setPageLoaded(true)
    }, [location])

    useEffect(() => {
        if (pageLoaded) {
            const now = new Date().toISOString().split('.')[0]
            if (selectedInterval === 'Past 24 Hours') {
                const yesterday = new Date(new Date().setHours(new Date().getHours() - 24)).toISOString().split('.')[0]
                setSelectedRange([yesterday, now])
            } else if (selectedInterval === 'Past Week') {
                const lastWeek = new Date(new Date().setHours(new Date().getHours() - 24 * 7)).toISOString().split('.')[0]
                setSelectedRange([lastWeek, now])
            } else if (selectedInterval === 'Past Month') {
                const daysLastMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate()
                const lastMonth = new Date(new Date().setDate(new Date().getDate() - daysLastMonth)).toISOString().split('.')[0]
                setSelectedRange([lastMonth, now])
            }
        }
    }, [selectedInterval, pageLoaded])

    useEffect(() => {
        if (alerts !== undefined) {
            const searchParams = new URLSearchParams(location.search)
            if (searchParams.size > 0) {
                const unit = searchParams.get('unit')
                const alert = flipDateOrName(searchParams.get('alert'), '-', ':')
                if (!findAlert(unit, alert) && !searchParams.has('filter')) {
                    dispatch(getSingleAlert({ serial_number: unit, name: alert }))
                } else {
                    findAlert(unit, alert)
                    setAlertModalOpen(true)
                }
            }
            setLoading(false)
        }
    }, [alerts])

    useEffect(() => {
        setLoading(true)
        if (selectedRange.length && pageLoaded) {
            setSelectedCounter(-1)
            setSelectedAlert(null)
            dispatch(getVehicleAlerts({ vehicles: selectedVehicles, devices: selectedDevices, type: selectedTypes, level: selectedLevels, app_version: selectedAppVersions, range: selectedRange, sortBy: sortBy }))
        }
    }, [selectedVehicles, selectedDevices, selectedTypes, selectedLevels, selectedAppVersions, selectedRange, sortBy, pageLoaded])

    useEffect(() => {
        if (url !== '') {
            setVideoModalOpen(true)
        }
    }, [url])

    // DetectionModal + VideoModal are shared by both the desktop and mobile layouts.
    const modals = (
        <>
            <DetectionModal
                open={alertModalOpen}
                handleClose={handleClose}
                title={<><b>Date:</b> {new Date(`${selectedAlert?.detection_date}+00:00`).toLocaleString()}<br /><b>Trigger:</b> {typeDict[selectedAlert?.trigger_type]} ({levelDict[selectedAlert?.trigger_level]})</>}
                left_data={[
                    ['Unit: ', selectedAlert?.vehicle_name ? selectedAlert?.vehicle_name : selectedAlert?.serial_number],
                    ['Location: ', `${selectedAlert?.location.latitude}, ${selectedAlert?.location.longitude}`],
                    ['App Version: ', selectedAlert?.app_version],
                ]}
                alert={selectedAlert}
                setUrl={setUrl}
            />
            <VideoModal name={url[0]} url={url[1]} open={videoModalOpen} setOpen={setVideoModalOpen} />
        </>
    )

    if (isMobile) {
        return (
            <>
                <AlarmHistoryMobile
                    alerts={alerts}
                    loading={loading}
                    INTERVALS={INTERVALS}
                    selectedInterval={selectedInterval}
                    setInterval={setInterval}
                    onCustomDates={onCustomDates}
                    units={units}
                    appVersions={appVersions}
                    isSpecial={isSpecial}
                    onClick={onClick}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    getSetState={getSetState}
                    handleDetectionClick={handleDetectionClick}
                    typeDict={typeDict}
                    levelDict={levelDict}
                    selectedKey={alertKey(selectedAlert)}
                    viewedKeys={viewedKeys}
                />
                {modals}
            </>
        )
    }

    return (
        <>
            <AlarmHistoryDesktop
                alerts={alerts}
                loading={loading}
                INTERVALS={INTERVALS}
                selectedInterval={selectedInterval}
                setInterval={setInterval}
                onCustomDates={onCustomDates}
                units={units}
                appVersions={appVersions}
                isSpecial={isSpecial}
                onClick={onClick}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                getSetState={getSetState}
                handleDetectionClick={handleDetectionClick}
                typeDict={typeDict}
                levelDict={levelDict}
                selectedKey={alertKey(selectedAlert)}
                viewedKeys={viewedKeys}
            />
            {modals}
        </>
    )
};

export default Index;
